import { ReactElement, useState } from 'react';
import {
  CloseButton, ModalContainer, ModalContent, ModalHeader, ModalOverlay,
} from './styles';
import { CloseModalX, Edit5Icon } from 'icons';
import { useTranslation } from 'react-i18next';
import { useModalControl } from '~/hooks/useModalControl';
import { colors } from '~/styles/colors';
import { FormContent } from '../Form';
import { EditClientFormValues } from '../Form/types';
import { useToast } from '~/contexts/newToastContext';
import { apiCall } from '~/providers';
import { buildFormDataByInfo, buildParamsByFormData } from '../Form/constants';
import { useParams } from 'react-router-dom';
import { Info } from '../ClientHeader';
import { useDebounce } from '~/hooks';

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  info: Info;
  onSuccess: (data: EditClientFormValues) => void;
}

export function ClientModal({
  info,
  isOpen,
  onClose,
  onSuccess,
}: Readonly<ClientModalProps>): ReactElement | null {
  const { clientId } = useParams<{ clientId: string }>();
  const { modalReference } = useModalControl(isOpen, onClose);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const debouncedOpen = useDebounce(isOpen, 100);

  async function onSubmit(data: EditClientFormValues): Promise<void> {
    try {
      setLoading(true);

      const params = buildParamsByFormData(data);

      if (clientId) {
        await apiCall('/clients/edit-client', {
          CLIENT_ID: Number(clientId),
          ...params,
        });
        toast(t('sucessoTitulo'), t('clienteAlteradoSucesso'), 'success');
      } else {
        await apiCall('/clients/add-new-client', params);
        toast(t('sucessoTitulo'), t('clienteAdicionadoSucesso'), 'success');
      }

      onSuccess(data);
    } catch (error) {
      console.log(error);
      toast(t('erroTitulo'), t('houveErro'), 'error');
    }

    setLoading(false);
  }

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={debouncedOpen}>
      <ModalContainer ref={modalReference} id="edit-client-modal">
        <ModalHeader>
          <div>
            <Edit5Icon size="24" color={colors.stroke['primary-base']} />
            <h3>{t('editarEmpresa')}</h3>
          </div>
          <CloseButton type="button" onClick={onClose}>
            <CloseModalX />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <FormContent
            defaultValues={buildFormDataByInfo(info)}
            onCancel={onClose}
            onSubmit={onSubmit}
            disabled={loading}
          />
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}
